import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIButton, SCUIDateRanger } from '../../../../../atomic/atoms';
import { SCUIGeneralDrawer, SCUIGeneralDrawerHeader } from '../../../../../atomic/molecules';
import ShippingCommand from '../../../../../services/command/shipping/ShippingCommand.services';
import TimeHelper from '../../../../../utils/time';
import Swal from 'sweetalert2';
import { LCWFEE } from '../../../lcwCargoFeeManagement.classes';

interface CargoCompanyOption {
    id: number;
    name: string;
}

interface FeeRowItem {
    id: number;
    shippingCompanyId: number;
    cargoCompanyName: string;
    desi: number | '';
    cost: number | '';
    startDate: string | Date | null;
    endDate: string | Date | null;
    updateDate: string | Date | null;
    updatedBy: string;
}

type Props = {
    open: boolean;
    onClose: () => void;
    companyOptions: CargoCompanyOption[];
    initialData?: FeeRowItem;
    setIsLoading: (isLoading: boolean) => void;
    onSaved: () => void;
};

const FeeManagementListDrawer = ({ open, onClose, companyOptions, initialData, setIsLoading, onSaved }: Props) => {
    const { t } = useTranslation();

    const initialFormState: FeeRowItem = useMemo(
        () => ({
            id: 0,
            shippingCompanyId: 0,
            cargoCompanyName: '',
            desi: 0,
            cost: 0,
            startDate: TimeHelper.formatDate(new Date()),
            endDate: TimeHelper.formatDate(new Date()),
            updateDate: null,
            updatedBy: '',
        }),
        []
    );

    const isEditMode = Boolean(initialData);

    const [formState, setFormState] = useState<FeeRowItem>(initialData || initialFormState);

    useEffect(() => {
        if (isEditMode && initialData) {
            const normalized: FeeRowItem = {
                id: initialData.id ?? 0,
                shippingCompanyId: initialData.shippingCompanyId ?? 0,
                cargoCompanyName: initialData.cargoCompanyName ?? '',
                desi: initialData.desi ?? 0,
                cost: initialData.cost ?? 0,
                startDate: TimeHelper.formatDate(initialData.startDate || (initialFormState.startDate as string)),
                endDate: TimeHelper.formatDate(initialData.endDate || (initialFormState.endDate as string)),
                updateDate: initialData.updateDate ?? null,
                updatedBy: initialData.updatedBy ?? '',
            };
            setFormState(normalized);
        } else {
            setFormState(initialFormState);
        }
    }, [isEditMode, initialData, initialFormState]);

    const handleChangeCompany = (e: SelectChangeEvent<number>) => {
        const id = Number(e.target.value);
        const selected = companyOptions.find((x) => x.id === id);
        setFormState((prev) => ({ ...prev, shippingCompanyId: id, cargoCompanyName: selected?.name || '' }));
    };

    const handleChangeField = (key: keyof FeeRowItem, value: any) => {
        setFormState((prev) => ({ ...prev, [key]: value }));
    };

    const handleChangeStartEnd = (range: [string, string]) => {
        handleChangeField('startDate', range[0] || null);
        handleChangeField('endDate', range[1] || null);
    };

    const isAddModeValid = (): boolean => {
        return (
            Number(formState.shippingCompanyId) > 0 &&
            Number(formState.desi) > 0 &&
            Number(formState.cost) > 0 &&
            Boolean(formState.startDate) &&
            Boolean(formState.endDate)
        );
    };

    const toMMDDYYYY = (val?: string | Date | null): string => {
        if (!val) return '';
        if (val instanceof Date) {
            const m = String(val.getMonth() + 1).padStart(2, '0');
            const d = String(val.getDate()).padStart(2, '0');
            const y = val.getFullYear();
            return `${m}/${d}/${y}`;
        }
        const s = String(val).replace(/-/g, '/');
        const parts = s.split('/');
        if (parts.length !== 3) return s;
        const [a, b, c] = parts; // a/b/c could be dd/mm/yyyy or mm/dd/yyyy
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        // If first part > 12, assume dd/mm/yyyy -> swap
        if (!isNaN(numA) && numA > 12) return `${b}/${a}/${c}`;
        // If second part > 12, assume mm/dd/yyyy already
        if (!isNaN(numB) && numB > 12) return s;
        // Default: treat as dd/mm/yyyy
        return `${b}/${a}/${c}`;
    };

    const handleSave = () => {
        const payload = {
            shippingCompanyId: Number(formState.shippingCompanyId),
            desi: Number(formState.desi),
            cost: Number(formState.cost),
            validFrom: toMMDDYYYY(formState.startDate),
            validTo: toMMDDYYYY(formState.endDate),
            changedBy: formState.updatedBy || '',
        };
        setIsLoading(true);
        ShippingCommand.UpsertShippingCompanyCostForDesi(payload)
            .then(() => {
                Swal.fire({
                    title: t('general.success'),
                    icon: 'success',
                    timer: 1500,
                });
                onSaved();
                onClose();
                setFormState(initialFormState);
            })
            .catch(() => {
                Swal.fire({
                    title: t('general.error'),
                    text: t('general.anErrorOccured'),
                    icon: 'error',
                });
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <SCUIGeneralDrawer width="480px" open={open} onClose={onClose}>
            <SCUIGeneralDrawerHeader
                header={
                    isEditMode
                        ? t('lcwCargoFeeManagement.list.drawer.editTitle')
                        : t('lcwCargoFeeManagement.list.drawer.addTitle')
                }
                onClose={onClose}
            />
            <Box className={LCWFEE.drawerBody} sx={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box className={LCWFEE.drawerFormFields} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <FormControl className={LCWFEE.drawerCargoCompanyWrap} fullWidth>
                        <InputLabel sx={{ fontWeight: 600 }} id="cargo-company-label">
                            {t('lcwCargoFeeManagement.list.drawer.cargoCompany')}
                        </InputLabel>
                        <Select
                            className={LCWFEE.inputSelectDrawerCargoCompany}
                            labelId="cargo-company-label"
                            value={formState?.shippingCompanyId || ''}
                            label={t('lcwCargoFeeManagement.list.drawer.cargoCompany')}
                            onChange={handleChangeCompany as any}
                            disabled={isEditMode}
                        >
                            {companyOptions
                                .filter((opt) => opt.id !== -1)
                                .map((opt) => (
                                    <MenuItem key={opt.id} value={opt.id}>
                                        {opt.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>

                    <TextField
                        className={LCWFEE.inputTextDrawerDesi}
                        label={t('lcwCargoFeeManagement.list.drawer.desi')}
                        slotProps={{ inputLabel: { sx: { fontWeight: 600 } } }}
                        type="text"
                        value={formState?.desi ?? ''}
                        onChange={(e) => {
                            handleChangeField('desi', e.target.value);
                        }}
                        onKeyDown={(e: any) => {
                            const isDigit = e.key >= '0' && e.key <= '9';
                            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

                            if (!isDigit && !allowedKeys.includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        disabled={isEditMode}
                        fullWidth
                    />

                    <TextField
                        className={LCWFEE.inputTextDrawerCost}
                        label={t('lcwCargoFeeManagement.list.drawer.cost')}
                        slotProps={{
                            inputLabel: { sx: { fontWeight: 600 } },
                            htmlInput: { inputMode: 'decimal', pattern: '[0-9.]*' },
                        }}
                        type="text"
                        value={formState?.cost ?? ''}
                        onChange={(e) => {
                            handleChangeField('cost', e.target.value);
                        }}
                        onKeyDown={(e: any) => {
                            const isDigit = e.key >= '0' && e.key <= '9';
                            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.'];
                            if (e.key === '.' && e.target.value?.includes('.')) {
                                e.preventDefault();
                                return;
                            }
                            if (!isDigit && !allowedKeys.includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        fullWidth
                    />

                    <Box className={LCWFEE.drawerDateSection}>
                        <Typography className={LCWFEE.drawerDateSectionTitle} variant="subtitle2" sx={{ marginBottom: '16px', fontWeight: 600 }}>
                            {t('lcwCargoFeeManagement.list.drawer.startEndDate')}
                        </Typography>
                        <Box className={LCWFEE.drawerDateRangeWrap}>
                            <SCUIDateRanger
                                fromDate={formState?.startDate}
                                toDate={formState?.endDate}
                                setDateRange={handleChangeStartEnd}
                                dateFormat="DD/MM/YYYY"
                                containerStyling={{ width: '100%' }}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box
                    className={LCWFEE.drawerFooter}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        width: '100%',
                        paddingTop: '32px',
                    }}
                >
                    <SCUIButton
                        className={LCWFEE.inputButtonDrawerClear}
                        text={t('general.clear')}
                        onClick={() =>
                            isEditMode ? setFormState(initialData || initialFormState) : setFormState(initialFormState)
                        }
                        buttonType="button"
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        style={{ backgroundColor: 'red', borderColor: 'red' }}
                    />
                    <SCUIButton
                        className={LCWFEE.inputButtonDrawerSave}
                        text={t('lcwCargoFeeManagement.list.drawer.save')}
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        buttonType="button"
                        onClick={handleSave}
                        disabled={!isEditMode && !isAddModeValid()}
                    />
                </Box>
            </Box>
        </SCUIGeneralDrawer>
    );
};

export default FeeManagementListDrawer;
